// ============================================================================
// FSA NOTE MODAL — DISCLOSURE VALIDATION ENGINE
// ============================================================================
// JSON-declared validation rules on additional disclosures. The seed
// authors cross-row / cross-column consistency checks directly in the
// disclosure spec; the engine evaluates them against the current grid
// state and surfaces warnings inline.
//
// All rules are warn-only per the FSA-2.2 decision — the auditor can
// always save the note. The approver sees the warnings list at
// submission time (wired in FSA-3 approval surface).
//
// JSON shape on each disclosure spec:
//   "validations": [
//     {
//       "code": "py_closing_eq_cy_opening_amount",
//       "rule": "match",
//       "message": "Previous-year closing must equal current-year opening (amount).",
//       "left":  { "row_key": "closing", "column": "amount_py" },
//       "right": { "row_key": "opening", "column": "amount_cy" },
//       "tolerance": 0.01                  // optional; default 0.01
//     }
//   ]
//
// Either side may instead reference a Main Note class of the same note —
// the grid cell is then cross-checked against the note's own classified
// amount (e.g. MSME principal outstanding ↔ N7_MSME):
//       "right": { "class_code": "N7_MSME", "period": "cy" }   // cy | py

export type ValidationSeverity = 'warning' | 'error';

export type ValidationRuleType = 'match';

/** A cell of this disclosure grid. */
export interface ValidationGridCellRef {
  /** Identifier for the row — matches the fixed_row's `row_key`. */
  row_key: string;
  /** Column code in the column_spec. */
  column: string;
}

/** A Main Note class amount on the same note (actual, not rounded). */
export interface ValidationClassRef {
  class_code: string;
  /** cy → amount_actual, py → prior_amount_actual. */
  period: 'cy' | 'py';
}

export type ValidationCellRef = ValidationGridCellRef | ValidationClassRef;

/** The Main Note class amounts a class-ref side reads from. */
export type ClassAmountLookup = Record<string, { amount_actual?: number; prior_amount_actual?: number } | undefined>;

export interface ValidationRule {
  code: string;
  rule: ValidationRuleType;
  message: string;
  left: ValidationCellRef;
  right: ValidationCellRef;
  tolerance?: number;
  severity?: ValidationSeverity;
}

export interface ValidationResult {
  code: string;
  message: string;
  severity: ValidationSeverity;
  leftValue: number;
  rightValue: number;
  delta: number;
  leftCell: ValidationCellRef;
  rightCell: ValidationCellRef;
}

type Row = Record<string, string | number | null>;

const DEFAULT_TOLERANCE = 0.01;

/**
 * Evaluate every rule against the current grid state. Returns one
 * ValidationResult per FAILING rule. Passing rules are omitted so the UI
 * can render the result array directly.
 *
 * @param rules           — validation rules from the disclosure spec.
 * @param rows            — current grid rows (one row per fixed_row, in spec order).
 * @param rowKeysInOrder  — row_key for each row index (same order as rows).
 * @param classAmounts    — the note's class_data; required only by rules
 *                          with a class_code side (those are skipped without it).
 */
export function evaluateValidations(
  rules: ValidationRule[],
  rows: Row[],
  rowKeysInOrder: string[],
  classAmounts?: ClassAmountLookup,
): ValidationResult[] {
  if (!rules || rules.length === 0) return [];
  const out: ValidationResult[] = [];

  const rowIndexByKey = new Map<string, number>();
  rowKeysInOrder.forEach((key, i) => {
    if (key) rowIndexByKey.set(key, i);
  });

  // null = the ref can't be resolved (unknown row / class) → skip the rule.
  const resolve = (ref: ValidationCellRef): number | null => {
    if ('class_code' in ref) {
      const cls = classAmounts?.[ref.class_code];
      if (!cls) return null;
      return toNumber(ref.period === 'py' ? cls.prior_amount_actual : cls.amount_actual);
    }
    const idx = rowIndexByKey.get(ref.row_key);
    if (idx == null) return null;
    return toNumber(rows[idx]?.[ref.column]);
  };

  for (const r of rules) {
    if (r.rule !== 'match') continue; // future rule types layer in here.

    const lVal = resolve(r.left);
    const rVal = resolve(r.right);
    if (lVal == null || rVal == null) continue;

    const tolerance = r.tolerance ?? DEFAULT_TOLERANCE;
    const delta = lVal - rVal;
    if (Math.abs(delta) <= tolerance) continue;

    out.push({
      code: r.code,
      message: r.message,
      severity: r.severity ?? 'warning',
      leftValue: lVal,
      rightValue: rVal,
      delta,
      leftCell: r.left,
      rightCell: r.right,
    });
  }

  return out;
}

function toNumber(v: string | number | null | undefined): number {
  if (typeof v === 'number') return v;
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
